import { useState, useEffect } from "react";
import { X, Save } from "lucide-react";
import { updateSOSTelefono, SOSTelefono } from "../../../api/sosService";
import Swal from "sweetalert2";

interface EditarTelefonoModalProps {
  isOpen: boolean;
  telefono: SOSTelefono;
  onClose: () => void;
  onModified: () => void;
}

export function EditarTelefonoModal({ isOpen, telefono, onClose, onModified }: EditarTelefonoModalProps) {
  const [entidad, setEntidad] = useState("");
  const [numero, setNumero] = useState("");
  const [prioridad, setPrioridad] = useState("");
  const [activo, setActivo] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (telefono) {
      setEntidad(telefono.ste_entidad);
      setNumero(telefono.ste_telefono);
      setPrioridad(telefono.ste_prioridad.toString());
      setActivo(telefono.ste_activado);
    }
  }, [telefono]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!entidad.trim() || !numero.trim() || !prioridad) {
      Swal.fire({
        icon: "warning",
        title: "Campos requeridos",
        text: "Por favor, complete todos los campos.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    setLoading(true);
    try {
      await updateSOSTelefono(telefono.ste_codigo, entidad.toUpperCase(), numero, parseInt(prioridad), activo);
      Swal.fire({
        icon: "success",
        title: "Éxito",
        text: "Teléfono modificado correctamente.",
        timer: 1500,
        showConfirmButton: false
      });
      onModified();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al modificar el teléfono.",
        confirmButtonColor: "#d33",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">
            Editar Entidad/Teléfono
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition"
            title="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Código</label>
            <input
              type="text"
              value={telefono.ste_codigo}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 bg-gray-100 rounded-md text-gray-500 cursor-not-allowed font-mono"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre (Entidad)</label>
            <input
              type="text"
              value={entidad}
              onChange={(e) => setEntidad(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">No. Telefónico</label>
            <input
              type="text"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad (Númerico)</label>
            <input
              type="number"
              min="1"
              value={prioridad}
              onChange={(e) => setPrioridad(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Activo</label>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="activo"
                  value="1"
                  checked={activo === 1}
                  onChange={() => setActivo(1)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-sm text-gray-700 font-medium">Sí</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="activo"
                  value="0"
                  checked={activo === 0}
                  onChange={() => setActivo(0)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-sm text-gray-700 font-medium">No</span>
              </label>
            </div>
          </div>

          <div className="pt-4 flex justify-end gap-3 border-t mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={loading}
            >
              <Save size={16} />
              {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
